import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Avatar from '@/components/switchback/Avatar';
import Button, { ButtonSize, ButtonVariants } from '@/components/switchback/Button/Button';
import Icon from '@/components/switchback/Icon/Icon';
import { ICONS, IconSize } from '@/components/switchback/Icon/Icon.constants';
import Text from '@/components/switchback/Text/Text';
import { BookingStatusLabel } from '@/components/trips/BookingCard/BookingStatusLabel/BookingStatusLabel';
import { authUserSelector } from '@/redux/selectors/auth/verifications';
import { getFormattedTotalFromBooking } from '@/redux/selectors/bill/total';
import { IConversation } from '@/services/messages';
import formatDate from '@/utility/format-date';

import css from './ConversationsList.module.css';

interface IConversationListItemProps {
  conversation: IConversation;
  activeStatus: string;
  onConversationClick: (id: number) => void;
  onArchiveConversation: (id: number) => void;
}

const ConversationListItem: React.FC<IConversationListItemProps> = ({
  conversation,
  activeStatus,
  onConversationClick,
  onArchiveConversation,
}) => {
  const intl = useIntl();
  const currentUser = useSelector(authUserSelector);
  const isArchived = activeStatus === 'archived';
  const avatarUrl = useMemo(() => {
    return conversation.owner_id === currentUser?.id
      ? conversation.renter_summary.avatar_url
      : conversation.owner_summary.avatar_url;
  }, [conversation, currentUser]);
  const displayName = useMemo(() => {
    if (conversation.owner_id === currentUser?.id) {
      return `${conversation.renter_summary.first_name} ${conversation.renter_summary.last_name}`;
    }

    return conversation.owner_summary.outdoorsy_display_name
      ? conversation.owner_summary.outdoorsy_display_name
      : `${conversation.owner_summary.first_name}`;
  }, [conversation, currentUser]);
  const booking = useMemo(() => {
    const { bookings } = conversation;
    return bookings && bookings[bookings.length - 1];
  }, [conversation]);

  return (
    <div
      className={css.Message}
      data-read={conversation.read}
      role="button"
      tabIndex={0}
      aria-label={intl.formatMessage({ defaultMessage: 'View message', id: 'n+bfB1' })}
      onClick={() => onConversationClick(conversation.id)}
      onKeyPress={e => e.key === 'Enter' && onConversationClick(conversation.id)}>
      <div className={css.avatar}>
        <Avatar name={displayName} photoUrl={avatarUrl} />
      </div>
      <div className={css.userdate}>
        <Text className={css.user} type="block">
          {displayName}
        </Text>
        <Text className={css.date} type="block">
          {formatDate(conversation.updated, 'MMM D')}
        </Text>
      </div>
      <div className={css.main}>
        <Text className={css.message} type="paragraph">
          {conversation.messages?.[0]?.text ||
            intl.formatMessage({ defaultMessage: 'No messages.', id: '8ntPO2' })}
        </Text>
        {booking && (
          <Text className={css.meta} type="paragraph">
            {booking.rental_summary?.name}
            {booking.from && booking.to
              ? ` (${formatDate(booking.from, 'MMM D')} - ${formatDate(booking.to, 'MMM D')})`
              : ''}
          </Text>
        )}
      </div>
      {booking && (
        <div className={css.statusprice}>
          <div className={css.status}>
            <BookingStatusLabel status={booking.status} statusDisplay={booking.display_status} />
          </div>
          <div className={css.price}>
            <Text type="inline">
              {getFormattedTotalFromBooking(booking, booking.locale.base_currency)}
            </Text>
          </div>
        </div>
      )}
      <div className={css.archive}>
        <Button
          size={ButtonSize.xxsmall}
          className={isArchived ? '!px-4' : '!px-6'}
          variant={ButtonVariants.darkGrayOutlined}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            onArchiveConversation(conversation.id);
          }}>
          <Icon name={ICONS.ARCHIVE} size={IconSize.small} />
          <span className="ml-1">
            {isArchived ? (
              <FormattedMessage defaultMessage="Unarchive" id="ftDYZo" />
            ) : (
              <FormattedMessage defaultMessage="Archive" id="hrgo+E" />
            )}
          </span>
        </Button>
      </div>
    </div>
  );
};

interface IConversationListProps {
  conversations: IConversation[];
  activeStatus: string;
  onConversationClick: (id: number) => void;
  onArchiveConversation: (id: number) => void;
}

const ConversationsList: React.FC<IConversationListProps> = ({
  conversations,
  activeStatus,
  onConversationClick,
  onArchiveConversation,
}) => {
  return (
    <>
      {conversations.map(conversation => {
        return (
          <ConversationListItem
            key={conversation.id}
            conversation={conversation}
            activeStatus={activeStatus}
            onConversationClick={onConversationClick}
            onArchiveConversation={onArchiveConversation}
          />
        );
      })}
    </>
  );
};

export default ConversationsList;
